// App shell: Polaris AppProvider (en i18n) + App Bridge Provider (session-token
// source) + API/Store context + React Router. App Bridge needs the Shopify
// `host` query param and the app client_id (VITE_SHOPIFY_API_KEY). When either is
// missing (app opened outside Shopify admin, or env not yet configured) we render
// a self-contained notice instead of letting App Bridge throw.

import "@shopify/polaris/build/esm/styles.css";
import { AppProvider as PolarisProvider, Banner, Frame, Layout, Page, Text } from "@shopify/polaris";
import enTranslations from "@shopify/polaris/locales/en.json";
import { Provider as AppBridgeProvider } from "@shopify/app-bridge-react";
import { BrowserRouter, Navigate, Route, Routes, useLocation } from "react-router-dom";
import { ApiProvider, MockApiProvider } from "./api/ApiProvider";
import { StoreProvider } from "./context/StoreProvider";
import { AppFrame } from "./components/AppFrame";
import { DashboardPage } from "./pages/DashboardPage";
import { ContentPage } from "./pages/ContentPage";
import { RevenuePage } from "./pages/RevenuePage";
import { SettingsPage } from "./pages/SettingsPage";

function getHost(): string | null {
  const params = new URLSearchParams(window.location.search);
  return params.get("host");
}

// Preview mode: `?preview=1` query param, or a `/preview` path prefix. When on,
// the app skips App Bridge entirely and runs against built-in mock data, so the
// full UI is viewable/testable without a Shopify host or backend.
//
// GATED: preview is only reachable in local dev (`import.meta.env.DEV`) or when
// the build explicitly opts in via VITE_ENABLE_PREVIEW=true. A production build
// with the flag unset ignores `?preview=1` entirely, so the public deployment
// never exposes the mock console. (The mock data is fake — fixed UUIDs,
// example.com domains, fake GIDs — but the demo surface still shouldn't be
// publicly reachable by default.)
function previewEnabled(): boolean {
  return import.meta.env.DEV || import.meta.env.VITE_ENABLE_PREVIEW === "true";
}

function isPreview(): boolean {
  if (!previewEnabled()) return false;
  const params = new URLSearchParams(window.location.search);
  const flag = params.get("preview");
  if (flag === "1" || flag === "true") return true;
  return window.location.pathname.replace(/\/+$/, "").endsWith("/preview") ||
    window.location.pathname === "/preview";
}

const API_KEY = import.meta.env.VITE_SHOPIFY_API_KEY || "";

// Catch-all redirect that PRESERVES the current query string. A bare
// `<Navigate to="/">` drops the Shopify `host`/`shop`/`embedded` (and `preview`)
// params, so landing on an unknown path and then refreshing would strand the app
// on SetupNotice. Keep the search so the redirect target stays bootable.
function RedirectToHome() {
  const location = useLocation();
  return <Navigate to={{ pathname: "/", search: location.search }} replace />;
}

// Shared routed shell used by both the real and preview paths.
function RoutedApp() {
  return (
    <BrowserRouter>
      <AppFrame>
        <Routes>
          <Route path="/" element={<DashboardPage />} />
          <Route path="/content" element={<ContentPage />} />
          <Route path="/revenue" element={<RevenuePage />} />
          <Route path="/settings" element={<SettingsPage />} />
          <Route path="*" element={<RedirectToHome />} />
        </Routes>
      </AppFrame>
    </BrowserRouter>
  );
}

// Unobtrusive banner so it's always clear preview data isn't a real store.
function PreviewBanner() {
  return (
    <div style={{ padding: "0.5rem 1rem 0" }}>
      <Banner tone="info">
        <Text as="p">Preview mode — sample data, not connected to a store.</Text>
      </Banner>
    </div>
  );
}

function PreviewApp() {
  // Polaris <Toast> renders through <Frame> context; wrap the preview tree in a
  // Frame so the approve/save toasts work in preview. (The App Bridge path is
  // left exactly as-is, per the requirement to not alter the normal flow.)
  return (
    <MockApiProvider>
      <Frame>
        <PreviewBanner />
        <StoreProvider>
          <RoutedApp />
        </StoreProvider>
      </Frame>
    </MockApiProvider>
  );
}

function SetupNotice({ reason }: { reason: string }) {
  return (
    <Page title="RankVine: AI SEO Autopilot">
      <Layout>
        <Layout.Section>
          <Banner tone="warning" title="Open RankVine from your Shopify admin">
            <Text as="p">{reason}</Text>
            <Text as="p">
              This console runs embedded inside Shopify. Install the app and open it from
              Settings → Apps, or the app's page in your Shopify admin.
            </Text>
          </Banner>
        </Layout.Section>
      </Layout>
    </Page>
  );
}

export default function App() {
  // Preview mode bypasses App Bridge entirely — checked first so it works with
  // no host/apiKey. The normal (non-preview) path below is unchanged.
  if (isPreview()) {
    return (
      <PolarisProvider i18n={enTranslations}>
        <PreviewApp />
      </PolarisProvider>
    );
  }

  const host = getHost();

  return (
    <PolarisProvider i18n={enTranslations}>
      {!API_KEY ? (
        <SetupNotice reason="The app is not fully configured yet (missing Shopify API key)." />
      ) : !host ? (
        <SetupNotice reason="Missing the Shopify host parameter — this page must be opened from the Shopify admin." />
      ) : (
        <AppBridgeProvider config={{ apiKey: API_KEY, host, forceRedirect: true }}>
          <ApiProvider>
            <StoreProvider>
              {/* Polaris <Toast> 通过 <Frame> context 渲染——正常 embedded 路径也必须
                  包 Frame，否则商家点批准/保存的 toast 会白屏崩溃（preview 分支同款修复）。*/}
              <Frame>
                <RoutedApp />
              </Frame>
            </StoreProvider>
          </ApiProvider>
        </AppBridgeProvider>
      )}
    </PolarisProvider>
  );
}
